/**
 * Shared canvas widgets.
 *
 * Small, boring, and used everywhere — panels, bars, chips, pips. Keeping them here means
 * the HUD, the overlays and the menus share one set of corner radii and one set of border
 * treatments, which is most of what makes a UI feel like one product.
 */

import {
  INK, RESOURCE_GLYPH, RESOURCE_INK, SEAT_SHAPE, contrastText, font, seatColor, TYPE,
  withAlpha,
} from './palette';
import type { PlayerId, Resources } from '../game/types';

/* -------------------------------------------------------------------- shapes */

export function roundRect(
  ctx: CanvasRenderingContext2D,
  x: number, y: number, w: number, h: number, r: number,
): void {
  const radius = Math.min(r, Math.abs(w) / 2, Math.abs(h) / 2);
  ctx.beginPath();
  ctx.moveTo(x + radius, y);
  ctx.arcTo(x + w, y, x + w, y + h, radius);
  ctx.arcTo(x + w, y + h, x, y + h, radius);
  ctx.arcTo(x, y + h, x, y, radius);
  ctx.arcTo(x, y, x + w, y, radius);
  ctx.closePath();
}

/**
 * A chamfered "console" panel — the shape used for every HUD surface.
 * The clipped corner sits top-right by default, which is what makes the frame read as
 * hardware rather than a web card.
 */
export function panelPath(
  ctx: CanvasRenderingContext2D,
  x: number, y: number, w: number, h: number,
  chamfer = 14,
  corners: { tl?: boolean; tr?: boolean; br?: boolean; bl?: boolean } = { tr: true },
): void {
  const c = Math.min(chamfer, Math.abs(w) / 3, Math.abs(h) / 3);
  ctx.beginPath();
  ctx.moveTo(x + (corners.tl ? c : 0), y);
  ctx.lineTo(x + w - (corners.tr ? c : 0), y);
  if (corners.tr) ctx.lineTo(x + w, y + c);
  ctx.lineTo(x + w, y + h - (corners.br ? c : 0));
  if (corners.br) ctx.lineTo(x + w - c, y + h);
  ctx.lineTo(x + (corners.bl ? c : 0), y + h);
  if (corners.bl) ctx.lineTo(x, y + h - c);
  ctx.lineTo(x, y + (corners.tl ? c : 0));
  ctx.closePath();
}

export interface PanelStyle {
  fill?: string;
  border?: string;
  borderWidth?: number;
  chamfer?: number;
  /** Seat colour accent stripe down the left edge. */
  accent?: string;
  accentWidth?: number;
  glow?: string;
  corners?: { tl?: boolean; tr?: boolean; br?: boolean; bl?: boolean };
}

export function panel(
  ctx: CanvasRenderingContext2D,
  x: number, y: number, w: number, h: number,
  style: PanelStyle = {},
): void {
  const {
    fill = INK.panel, border = INK.edge, borderWidth = 1.5, chamfer = 14,
    accent, accentWidth = 4, glow, corners = { tr: true },
  } = style;

  ctx.save();
  if (glow) {
    ctx.shadowColor = glow;
    ctx.shadowBlur = 22;
  }
  panelPath(ctx, x, y, w, h, chamfer, corners);
  ctx.fillStyle = fill;
  ctx.fill();
  ctx.shadowBlur = 0;

  if (borderWidth > 0) {
    ctx.strokeStyle = border;
    ctx.lineWidth = borderWidth;
    ctx.stroke();
  }

  if (accent) {
    // Clip to the panel so the stripe follows the chamfer.
    panelPath(ctx, x, y, w, h, chamfer, corners);
    ctx.clip();
    ctx.fillStyle = accent;
    ctx.fillRect(x, y, accentWidth, h);
  }
  ctx.restore();
}

/* ---------------------------------------------------------------------- text */

export interface TextOptions {
  size?: number;
  weight?: number;
  color?: string;
  align?: CanvasTextAlign;
  baseline?: CanvasTextBaseline;
  /** Letter-spacing in px, applied by drawing glyph by glyph. */
  tracking?: number;
  /** Truncate with an ellipsis beyond this width. */
  maxWidth?: number;
  shadow?: string;
  uppercase?: boolean;
}

export function text(
  ctx: CanvasRenderingContext2D,
  content: string,
  x: number,
  y: number,
  opts: TextOptions = {},
): number {
  const {
    size = TYPE.body, weight = 600, color = INK.text, align = 'left',
    baseline = 'alphabetic', tracking = 0, maxWidth, shadow, uppercase,
  } = opts;

  let body = uppercase ? content.toUpperCase() : content;

  ctx.save();
  ctx.font = font(size, weight);
  ctx.fillStyle = color;
  ctx.textAlign = tracking > 0 ? 'left' : align;
  ctx.textBaseline = baseline;
  if (shadow) {
    ctx.shadowColor = shadow;
    ctx.shadowBlur = 10;
  }

  if (maxWidth !== undefined) body = ellipsize(ctx, body, maxWidth, tracking);
  const width = measure(ctx, body, tracking);

  if (tracking > 0) {
    // Manual tracking: canvas `letterSpacing` is not in Safari before 17.4.
    let cursor = x;
    if (align === 'center') cursor = x - width / 2;
    else if (align === 'right') cursor = x - width;
    for (const ch of body) {
      ctx.fillText(ch, cursor, y);
      cursor += ctx.measureText(ch).width + tracking;
    }
  } else {
    ctx.fillText(body, x, y);
  }
  ctx.restore();
  return width;
}

export function measure(ctx: CanvasRenderingContext2D, content: string, tracking = 0): number {
  const base = ctx.measureText(content).width;
  return tracking > 0 ? base + tracking * Math.max(0, [...content].length - 1) : base;
}

function ellipsize(
  ctx: CanvasRenderingContext2D,
  content: string,
  maxWidth: number,
  tracking: number,
): string {
  if (measure(ctx, content, tracking) <= maxWidth) return content;
  let lo = 0;
  let hi = content.length;
  while (lo < hi) {
    const mid = (lo + hi + 1) >> 1;
    if (measure(ctx, `${content.slice(0, mid)}…`, tracking) <= maxWidth) lo = mid;
    else hi = mid - 1;
  }
  return `${content.slice(0, lo)}…`;
}

/** Word-wrapped paragraph. Returns the height consumed. */
export function paragraph(
  ctx: CanvasRenderingContext2D,
  content: string,
  x: number, y: number, maxWidth: number,
  opts: TextOptions & { lineHeight?: number } = {},
): number {
  const { size = TYPE.body, lineHeight = (opts.size ?? TYPE.body) * 1.42 } = opts;
  ctx.save();
  ctx.font = font(size, opts.weight ?? 500);

  const words = content.split(/\s+/);
  const lines: string[] = [];
  let line = '';
  for (const word of words) {
    const candidate = line ? `${line} ${word}` : word;
    if (ctx.measureText(candidate).width > maxWidth && line) {
      lines.push(line);
      line = word;
    } else {
      line = candidate;
    }
  }
  if (line) lines.push(line);
  ctx.restore();

  lines.forEach((l, i) => {
    text(ctx, l, x, y + i * lineHeight, { ...opts, size });
  });
  return lines.length * lineHeight;
}

/* --------------------------------------------------------------------- chips */

/** A small pill with a label — used for phase, tech names, charter names. */
export function chip(
  ctx: CanvasRenderingContext2D,
  label: string,
  x: number, y: number,
  opts: { color?: string; size?: number; padding?: number; height?: number; filled?: boolean } = {},
): number {
  const { color = INK.info, size = TYPE.micro, padding = 9, height = size + 11, filled = false } = opts;

  ctx.save();
  ctx.font = font(size, 700);
  const w = ctx.measureText(label.toUpperCase()).width + padding * 2 + size * 0.6;
  ctx.restore();

  roundRect(ctx, x, y, w, height, height / 2);
  ctx.save();
  ctx.fillStyle = filled ? color : withAlpha(color, 0.16);
  ctx.fill();
  ctx.strokeStyle = withAlpha(color, filled ? 0.9 : 0.5);
  ctx.lineWidth = 1.2;
  ctx.stroke();
  ctx.restore();

  text(ctx, label, x + w / 2, y + height / 2, {
    size, weight: 700, color: filled ? contrastText(color) : color,
    align: 'center', baseline: 'middle', tracking: 0.7, uppercase: true,
  });
  return w;
}

/* ---------------------------------------------------------------------- bars */

/** Horizontal meter. `segments > 0` draws it as discrete pips instead of a bar. */
export function meter(
  ctx: CanvasRenderingContext2D,
  x: number, y: number, w: number, h: number,
  value: number, max: number,
  opts: { color?: string; track?: string; segments?: number; label?: string } = {},
): void {
  const { color = INK.info, track = 'rgba(255,255,255,0.09)', segments = 0 } = opts;
  const ratio = max > 0 ? Math.max(0, Math.min(1, value / max)) : 0;

  if (segments > 0) {
    const gap = Math.max(2, w * 0.012);
    const cell = (w - gap * (segments - 1)) / segments;
    for (let i = 0; i < segments; i++) {
      const filled = i < Math.round(ratio * segments);
      roundRect(ctx, x + i * (cell + gap), y, cell, h, Math.min(3, h / 2));
      ctx.fillStyle = filled ? color : track;
      ctx.fill();
    }
    return;
  }

  roundRect(ctx, x, y, w, h, h / 2);
  ctx.fillStyle = track;
  ctx.fill();

  if (ratio > 0) {
    roundRect(ctx, x, y, Math.max(h, w * ratio), h, h / 2);
    ctx.fillStyle = color;
    ctx.fill();
  }
}

/* ----------------------------------------------------------------- resources */

/**
 * A resource readout: glyph, amount, and an optional delta.
 * Returns the width consumed so callers can lay out a row.
 */
export function resourceReadout(
  ctx: CanvasRenderingContext2D,
  kind: keyof Resources,
  amount: number,
  x: number, y: number,
  opts: { size?: number; delta?: number; dim?: boolean; capped?: boolean } = {},
): number {
  const { size = TYPE.body, delta = 0, dim = false, capped = false } = opts;
  const color = dim ? INK.textFaint : RESOURCE_INK[kind];

  let cursor = x;
  cursor += text(ctx, RESOURCE_GLYPH[kind], cursor, y, {
    size: size * 0.92, color, baseline: 'middle',
  }) + size * 0.28;

  cursor += text(ctx, String(amount), cursor, y, {
    size, weight: 700, color: capped ? INK.warn : dim ? INK.textDim : INK.text,
    baseline: 'middle',
  });

  if (delta !== 0) {
    cursor += size * 0.24;
    cursor += text(ctx, `${delta > 0 ? '+' : ''}${delta}`, cursor, y, {
      size: size * 0.78, weight: 700,
      color: delta > 0 ? INK.good : INK.bad, baseline: 'middle',
    });
  }
  return cursor - x;
}

/** The full four-resource row. */
export function resourceRow(
  ctx: CanvasRenderingContext2D,
  resources: Resources,
  x: number, y: number,
  opts: { size?: number; gap?: number; deltas?: Partial<Resources>; cap?: number } = {},
): number {
  const { size = TYPE.body, gap = 14, deltas, cap } = opts;
  let cursor = x;
  for (const kind of ['ore', 'fuel', 'biomass', 'data'] as const) {
    cursor += resourceReadout(ctx, kind, resources[kind], cursor, y, {
      size,
      delta: deltas?.[kind] ?? 0,
      capped: cap !== undefined && resources[kind] >= cap,
    }) + gap;
  }
  return cursor - x - gap;
}

/* ---------------------------------------------------------------- seat marks */

/** Seat badge: shape + colour + short label. The colour-blind-safe seat identity. */
export function seatBadge(
  ctx: CanvasRenderingContext2D,
  seat: PlayerId,
  x: number, y: number,
  size: number,
  opts: { label?: string; active?: boolean } = {},
): void {
  const color = seatColor(seat);
  const shape = SEAT_SHAPE[seat] ?? 'circle';

  ctx.save();
  if (opts.active) {
    ctx.shadowColor = color;
    ctx.shadowBlur = 16;
  }
  ctx.fillStyle = color;
  ctx.beginPath();
  switch (shape) {
    case 'circle':
      ctx.arc(x, y, size, 0, Math.PI * 2);
      break;
    case 'square':
      ctx.rect(x - size * 0.86, y - size * 0.86, size * 1.72, size * 1.72);
      break;
    case 'triangle':
      ctx.moveTo(x, y - size);
      ctx.lineTo(x + size * 0.92, y + size * 0.72);
      ctx.lineTo(x - size * 0.92, y + size * 0.72);
      ctx.closePath();
      break;
    case 'diamond':
      ctx.moveTo(x, y - size * 1.08);
      ctx.lineTo(x + size * 0.94, y);
      ctx.lineTo(x, y + size * 1.08);
      ctx.lineTo(x - size * 0.94, y);
      ctx.closePath();
      break;
  }
  ctx.fill();
  ctx.restore();

  if (opts.label) {
    text(ctx, opts.label, x, y + 1, {
      size: size * 1.05, weight: 800, color: contrastText(color),
      align: 'center', baseline: 'middle',
    });
  }
}

/* --------------------------------------------------------------- button hints */

/** Controller button glyph, e.g. the green A. Drawn as a coloured disc with a letter. */
const BUTTON_INK: Record<string, string> = {
  a: '#6ac04a', b: '#e05252', x: '#4a90e2', y: '#e8c33c',
  lb: '#8c93a8', rb: '#8c93a8', lt: '#8c93a8', rt: '#8c93a8',
  start: '#8c93a8', back: '#8c93a8',
};

export function buttonGlyph(
  ctx: CanvasRenderingContext2D,
  button: string,
  x: number, y: number, radius: number,
): void {
  const color = BUTTON_INK[button] ?? INK.textDim;
  const label = button.length === 1 ? button.toUpperCase() : button.toUpperCase();
  const isFace = button.length === 1;

  ctx.save();
  if (isFace) {
    ctx.beginPath();
    ctx.arc(x, y, radius, 0, Math.PI * 2);
  } else {
    roundRect(ctx, x - radius * 1.5, y - radius * 0.72, radius * 3, radius * 1.44, radius * 0.5);
  }
  ctx.fillStyle = withAlpha(color, 0.9);
  ctx.fill();
  ctx.restore();

  text(ctx, label, x, y + 0.5, {
    size: radius * (isFace ? 1.24 : 0.98), weight: 800,
    color: contrastText(color), align: 'center', baseline: 'middle',
  });
}

/** A `[A] Confirm` style hint. Returns width consumed. */
export function buttonHint(
  ctx: CanvasRenderingContext2D,
  button: string,
  label: string,
  x: number, y: number,
  opts: { size?: number; dim?: boolean } = {},
): number {
  const { size = TYPE.label, dim = false } = opts;
  const radius = size * 0.62;
  const isFace = button.length === 1;
  const glyphWidth = isFace ? radius * 2 : radius * 3;

  ctx.save();
  if (dim) ctx.globalAlpha = 0.45;
  buttonGlyph(ctx, button, x + glyphWidth / 2, y, radius);
  const labelWidth = text(ctx, label, x + glyphWidth + size * 0.42, y, {
    size, weight: 600, color: INK.textDim, baseline: 'middle', uppercase: true, tracking: 0.5,
  });
  ctx.restore();
  return glyphWidth + size * 0.42 + labelWidth;
}

/** A row of hints, right-aligned inside `width`. */
export function hintBar(
  ctx: CanvasRenderingContext2D,
  hints: readonly { button: string; label: string; dim?: boolean }[],
  x: number, y: number,
  opts: { size?: number; gap?: number } = {},
): void {
  const { size = TYPE.label, gap = 22 } = opts;
  let cursor = x;
  for (const hint of hints) {
    cursor += buttonHint(ctx, hint.button, hint.label, cursor, y, { size, dim: hint.dim }) + gap;
  }
}

/* ------------------------------------------------------------------ scanlines */

/**
 * A very subtle scanline + vignette pass over the whole frame.
 * Sells the "command console" look and, more usefully, darkens the screen edges so the
 * HUD panels separate from the starfield.
 */
export function screenGrade(
  ctx: CanvasRenderingContext2D,
  width: number, height: number,
): void {
  ctx.save();
  const vignette = ctx.createRadialGradient(
    width / 2, height / 2, Math.min(width, height) * 0.32,
    width / 2, height / 2, Math.max(width, height) * 0.78,
  );
  vignette.addColorStop(0, 'rgba(0,0,0,0)');
  vignette.addColorStop(1, 'rgba(0,0,0,0.52)');
  ctx.fillStyle = vignette;
  ctx.fillRect(0, 0, width, height);

  ctx.globalAlpha = 0.045;
  ctx.fillStyle = '#000';
  for (let y = 0; y < height; y += 3) ctx.fillRect(0, y, width, 1);
  ctx.restore();
}
